import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';
import { ChevronDown, Check } from 'lucide-react';

export interface DropdownItem {
  id: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  danger?: boolean;
  onClick?: () => void;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
  width?: 'auto' | 'trigger' | number;
}

export function Dropdown({ trigger, items, align = 'left', width = 'auto' }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, openUpward: false });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate position when dropdown opens
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuHeight = 200; // Estimated menu height
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Open upward if not enough space below and more space above
      const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

      setPosition({
        top: openUpward ? rect.top + window.scrollY : rect.bottom + window.scrollY,
        left: align === 'right' ? rect.right - 160 : rect.left, // 160 is min menu width
        openUpward,
      });
    }
  }, [isOpen, align]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const getWidth = () => {
    if (width === 'auto') return 'min-w-[160px]';
    if (width === 'trigger') return 'w-full';
    return `w-[${width}px]`;
  };

  const menu = isOpen && createPortal(
    <div
      ref={menuRef}
      className={cn(
        'fixed z-[9999] py-1 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700',
        getWidth()
      )}
      style={{
        top: position.openUpward ? 'auto' : position.top + 4,
        bottom: position.openUpward ? `${window.innerHeight - position.top + 4}px` : 'auto',
        left: Math.max(8, position.left), // Ensure at least 8px from left edge
      }}
      role="menu"
    >
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => {
            if (!item.disabled) {
              item.onClick?.();
              setIsOpen(false);
            }
          }}
          disabled={item.disabled}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
            'hover:bg-gray-100 dark:hover:bg-dark-700',
            item.disabled && 'opacity-50 cursor-not-allowed',
            item.danger
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-700 dark:text-gray-300'
          )}
          role="menuitem"
        >
          {item.icon && <span className="w-4 h-4">{item.icon}</span>}
          {item.label}
        </button>
      ))}
    </div>,
    document.body
  );

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer"
      >
        {trigger}
      </div>
      {menu}
    </div>
  );
}

export interface MenuButtonProps {
  label: string;
  icon?: ReactNode;
  items: DropdownItem[];
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
}

export function MenuButton({ label, icon, items, variant = 'outline' }: MenuButtonProps) {
  const baseStyles = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700',
    secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-100',
    outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-dark-600 dark:text-gray-300 dark:hover:bg-dark-700',
    ghost: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-700',
  };

  return (
    <Dropdown
      trigger={
        <button
          className={cn(
            'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
            baseStyles[variant]
          )}
        >
          {icon}
          {label}
          <ChevronDown className="h-4 w-4" />
        </button>
      }
      items={items}
    />
  );
}
